import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Stack,
  Paper,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { weekdays, formatWeekdays, recurringTaskPropType } from './kanbanTaskConstants';

const emptyRecurringForm = {
  title: '',
  description: '',
  weekdays: [],
};

export default function KanbanTaskRecurringModal({
  open,
  onClose,
  recurringTasks,
  onAdd,
  onUpdate,
  onDelete,
  onMove,
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecurringId, setEditingRecurringId] = useState(null);
  const [recurringForm, setRecurringForm] = useState(emptyRecurringForm);
  const [recurringToDelete, setRecurringToDelete] = useState(null);

  useEffect(() => {
    if (!open) {
      setFormOpen(false);
      setRecurringToDelete(null);
    }
  }, [open]);

  const openNewForm = () => {
    setEditingRecurringId(null);
    setRecurringForm(emptyRecurringForm);
    setFormOpen(true);
  };

  const openEditForm = (recurringTask) => {
    setEditingRecurringId(recurringTask.id);
    setRecurringForm({
      title: recurringTask.title,
      description: recurringTask.description || '',
      weekdays: recurringTask.weekdays,
    });
    setFormOpen(true);
  };

  const handleFormFieldChange = (field, value) => {
    setRecurringForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleSave = () => {
    const request =
      editingRecurringId !== null
        ? onUpdate(editingRecurringId, recurringForm)
        : onAdd(recurringForm);
    request.then((saved) => {
      if (saved) setFormOpen(false);
    });
  };

  const handleMove = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= recurringTasks.length) return;
    onMove(index, targetIndex);
  };

  const handleConfirmDelete = () => {
    onDelete(recurringToDelete).then((deleted) => {
      if (deleted) setRecurringToDelete(null);
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
        >
          Tarefas recorrentes
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openNewForm}>
            Adicionar recorrente
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            As tarefas abaixo são geradas automaticamente a cada semana, nos dias definidos. A ordem
            da lista define a sequência de execução no quadro.
          </Typography>
          {recurringTasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhuma tarefa recorrente cadastrada ainda.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {recurringTasks.map((recurringTask, index) => {
                return (
                  <Paper key={recurringTask.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                          {index + 1}. {recurringTask.title}
                        </Typography>
                        {recurringTask.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {recurringTask.description}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip
                            label={formatWeekdays(recurringTask.weekdays)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      </Box>
                      <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        <Tooltip title="Mover para cima">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMove(index, -1)}
                              disabled={index === 0}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Mover para baixo">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMove(index, 1)}
                              disabled={index === recurringTasks.length - 1}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEditForm(recurringTask)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            sx={{ color: 'error.main' }}
                            onClick={() => setRecurringToDelete(recurringTask.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRecurringId !== null ? 'Editar tarefa recorrente' : 'Nova tarefa recorrente'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Título"
              value={recurringForm.title}
              onChange={(event) => handleFormFieldChange('title', event.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Descrição"
              value={recurringForm.description}
              onChange={(event) => handleFormFieldChange('description', event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Dias da semana
              </Typography>
              <ToggleButtonGroup
                value={recurringForm.weekdays}
                onChange={(event, value) => handleFormFieldChange('weekdays', value)}
                size="small"
                color="primary"
              >
                {weekdays.map((weekday) => (
                  <ToggleButton key={weekday.id} value={weekday.id}>
                    {weekday.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!recurringForm.title.trim() || recurringForm.weekdays.length === 0}
          >
            {editingRecurringId !== null ? 'Salvar' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={recurringToDelete !== null}
        onClose={() => setRecurringToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Excluir tarefa recorrente</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Novas tarefas não serão mais geradas para esta recorrência e as tarefas pendentes
            geradas por ela serão removidas do quadro. Deseja continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecurringToDelete(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

KanbanTaskRecurringModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  recurringTasks: PropTypes.arrayOf(recurringTaskPropType).isRequired,
  onAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
};
